import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Point { date: string; value: number | null }
interface Finding {
  id: string; headline: string; detail: string; basis: string;
  n: number; r: number | null; direction: string; suggestion: string | null;
}

interface Charts {
  weight: { raw: Point[]; average: Point[]; ratePerWeek: number | null; targetRatePerWeek: number | null };
  intake: { kcal: Point[]; protein: Point[]; kcalTarget: number | null; proteinTarget: number | null; loggedDays: number };
  composition: Array<{ takenAt: string; weightKg: number | null; leanKg: number | null; fatKg: number | null; fatPct: number | null }>;
  risk: Array<{ at: string; score: number; band: string }>;
}

/**
 * One chart component for the whole page, so every chart shares its axis
 * treatment and its gap handling. Gaps are drawn as gaps: a line that jumps a
 * missing week implies data that was never recorded.
 */
function Line({
  points, overlay = [], target = null, height = 120, colour = '#58a6ff', overlayColour = '#3fb950',
}: {
  points: Array<number | null>; overlay?: Array<number | null>; target?: number | null;
  height?: number; colour?: string; overlayColour?: string;
}): JSX.Element {
  const real = [...points, ...overlay].filter((v): v is number => v != null);
  if (target != null) real.push(target);
  if (real.length < 2) return <p className="muted small">Not enough points yet.</p>;

  const min = Math.min(...real);
  const max = Math.max(...real);
  const span = max - min || 1;
  const w = 600;
  const x = (i: number): number => (points.length > 1 ? (i / (points.length - 1)) * w : 0);
  const y = (v: number): number => height - ((v - min) / span) * height;

  const runs = (series: Array<number | null>): string[] => {
    const out: string[] = [];
    let cur: string[] = [];
    series.forEach((v, i) => {
      if (v == null) { if (cur.length > 1) out.push(cur.join(' ')); cur = []; return; }
      cur.push(`${cur.length ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    });
    if (cur.length > 1) out.push(cur.join(' '));
    return out;
  };

  return (
    <svg viewBox={`0 0 ${w} ${height}`} width="100%" height={height} preserveAspectRatio="none">
      {target != null && (
        <line x1={0} x2={w} y1={y(target)} y2={y(target)} stroke="#334" strokeDasharray="6 6" strokeWidth={1.5} />
      )}
      {runs(points).map((d) => (
        <path key={d.slice(0, 24)} d={d} fill="none" stroke={colour}
              strokeWidth={overlay.length ? 1.2 : 2} opacity={overlay.length ? 0.4 : 1} />
      ))}
      {runs(overlay).map((d) => (
        <path key={`o-${d.slice(0, 24)}`} d={d} fill="none" stroke={overlayColour} strokeWidth={2.4} />
      ))}
    </svg>
  );
}

export function Trends(): JSX.Element {
  const [charts, setCharts] = useState<Charts | null>(null);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    api<Charts>('/insights/charts').then(setCharts).catch(() => undefined);
    api<{ findings: Finding[]; note: string }>('/insights')
      .then((r) => { setFindings(r.findings); setNote(r.note); })
      .catch(() => undefined);
  }, []);

  if (!charts) return <div className="card"><h2>Trends</h2><p className="muted small">Loading…</p></div>;

  const comp = charts.composition;
  const first = comp[0];
  const last = comp[comp.length - 1];

  return (
    <>
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ margin: 0 }}>Weight</h2>
          {charts.weight.ratePerWeek != null && (
            <strong>{charts.weight.ratePerWeek > 0 ? '+' : ''}{charts.weight.ratePerWeek} kg/week</strong>
          )}
        </div>
        <p className="muted small">
          The faint line is what the scale said. The solid one is the seven-day average, which is the
          part that means anything.
        </p>
        <Line points={charts.weight.raw.map((p) => p.value)} overlay={charts.weight.average.map((p) => p.value)} />
        {charts.weight.targetRatePerWeek != null && (
          <p className="muted small">Target {charts.weight.targetRatePerWeek} kg a week.</p>
        )}
      </div>

      <div className="card">
        <h2>Calories</h2>
        <Line points={charts.intake.kcal.map((p) => p.value)} target={charts.intake.kcalTarget} />
        <p className="muted small">
          Logged on {charts.intake.loggedDays} of {charts.intake.kcal.length} days. The dashed line is your target.
        </p>
      </div>

      <div className="card">
        <h2>Protein</h2>
        <Line points={charts.intake.protein.map((p) => p.value)} target={charts.intake.proteinTarget} colour="#3fb950" />
      </div>

      {comp.length >= 2 && (
        <div className="card">
          <h2>Body composition</h2>
          <p className="muted small">Lean mass against fat mass across your scans. The trend a scale cannot show.</p>
          <Line points={comp.map((c) => c.leanKg)} overlay={comp.map((c) => c.fatKg)} overlayColour="#f0883e" />
          {first?.leanKg != null && last?.leanKg != null && first.fatKg != null && last.fatKg != null && (
            <p className="small">
              Since the first scan: lean {(last.leanKg - first.leanKg).toFixed(1)} kg,
              fat {(last.fatKg - first.fatKg).toFixed(1)} kg.
            </p>
          )}
        </div>
      )}

      {charts.risk.length >= 2 && (
        <div className="card">
          <h2>Risk score</h2>
          <Line points={charts.risk.map((r) => r.score)} colour="#d29922" />
        </div>
      )}

      <div className="card">
        <h2>What the data says</h2>
        <p className="muted small">{note}</p>
        {findings.map((f) => (
          <div key={f.id} style={{ marginTop: 14 }}>
            <strong>{f.headline}</strong>
            <p>{f.detail}</p>
            <p className="muted small">{f.basis}</p>
            {f.suggestion && <p className="small">{f.suggestion}</p>}
          </div>
        ))}
      </div>
    </>
  );
}
